/*
 * MODIFICATION HISTORY - 2025-11-12
 * 
 * ISSUE: PDF pagination was creating blank pages and forced page breaks.
 * Symptoms:
 * - Content artificially split into 2 separate <Page> components (forced page break)
 * - All sections used wrap={false}, causing blank pages when content exceeded limits
 * - Table had container border causing vertical lines on splits
 * - Table headers not repeating when table split across pages
 * 
 * ROOT CAUSES:
 * 1. Manual page splitting instead of natural flow
 * 2. All content sections used wrap={false}, making large blocks unbreakable
 * 3. Table style had borderWidth: 1 on container
 * 4. Table headers lacked 'fixed' prop for pagination
 * 
 * SOLUTIONS APPLIED:
 * 1. Merged both pages into single <Page> with natural flow
 * 2. Removed wrap={false} from sections with potentially long content
 * 3. Removed border from table container (styles.table)
 * 4. Added 'fixed' prop to table headers for repeating on page splits
 * 5. Kept wrap={false} only for small critical sections (headings + short content)
 * 6. Individual table rows keep wrap={false} to prevent row splits
 * 
 * RESULT: Natural pagination flow, no blank pages, table headers repeat on splits
 */

import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import React, { useState, useEffect } from 'react';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    paddingTop: 150,
    paddingBottom: 70, // Added for footer space
    fontSize: 10, // Reduced from 12
    lineHeight: 1.3, // Adjusted from 1.5
    fontFamily: "Helvetica",
  },
  letterhead: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  hospitalDetails: {
    flex: 3,
  },
  hr: {
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    marginBottom: 10,
  },
  bold: {
    fontWeight: "bold",
    fontSize: 10, // Added to match boldfont
  },
  compactRow: {
    marginBottom: 2,
  },
  title: {
    fontSize: 14, // Reduced from 16
    textAlign: "center",
    marginBottom: 15,
    fontWeight: "bold",
    textTransform: "uppercase",
  },
  /* MODIFICATION: 2025-11-12 - Added keepWithNext to prevent orphaned headings
     OLD CODE: No keepWithNext property
     NEW CODE: Added keepWithNext: true to keep heading with first line of content
  */
  subtitle: {
    fontSize: 12, // Reduced from 14
    marginBottom: 5,
    fontWeight: "bold",
    marginTop: 10,
    keepWithNext: true,
  },
  boldfont: {
    fontSize: 10, // Reduced from 12
    fontWeight: "bold",
  },
  text: {
    marginBottom: 5,
    fontSize: 10, // Added to match page
  },
  /* MODIFICATION: 2025-11-12 - Removed border from table container
     OLD CODE: borderWidth: 1 caused vertical lines to extend down the page on table splits
     NEW CODE: Removed border properties, relying on tableCol borders to create grid
  */
  table: {
    display: "table",
    width: "100%",
    marginBottom: 10,
    marginTop: 5,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    padding: 5,
    fontSize: 10, // Added to ensure table text scales
  },
  tableHeader: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    fontSize: 10, // Added to ensure header text scales
  },
  section: {
    marginBottom: 10,
  },
  patientInfoContainer: {
    flexDirection: "row",
    marginBottom: 10,
    justifyContent: "space-between",
  },
  patientInfoLeft: {
    width: "50%",
  },
  patientInfoRight: {
    width: "50%",
    textAlign: "right",
  },
  patientInfoItem: {
    marginBottom: 5,
  },
  dateContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 10,
  },
  dateColumn: {
    width: "48%",
  },
  dateColumnRight: {
    width: "48%",
    textAlign: "right",
  },
  dateRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 5,
  },
  disclaimer: {
    marginTop: 15,
    fontSize: 10, // Already correct
    fontStyle: "italic",
  },
  disclaimerImage: {
    width: "100%",
    marginTop: 10,
  },
  signatureArea: {
    marginTop: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signatureBox: {
    width: "45%",
    borderBottomWidth: 1,
    borderBottomColor: "#000",
    paddingTop: 5,
  },
  dateRight: {
    textAlign: "right",
    marginBottom: 10,
    fontSize: 10, // Added to match page
  },
  smallText: {
    fontSize: 10, // Already correct
    marginBottom: 5,
  },
  footer: {
    position: "absolute",
    bottom: 30,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 8, // Matches OPDDischargeForm footer
    color: "#666",
  },
});



const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  
  try {
    // Handle yyyy/mm/dd format
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const [year, month, day] = dateString.split('/');
      return `${day}/${month}/${year}`; // Convert to DD/MM/YYYY
    }
    
    // Handle yyyy-mm-dd format (for currentDate)
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const [year, month, day] = dateString.split('-');
      return `${day}/${month}/${year}`; // Convert to DD/MM/YYYY
    }
    
    // Fallback for other formats
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY format
  } catch (error) {
    return dateString; // Return original if parsing fails
  }
};


// Function to convert 24-hour time to 12-hour format with AM/PM
const convertTo12HourFormat = (time) => {
  if (!time || time === "_____________") return time;

  try {
    const [hours, minutes] = time.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return time;

    const period = hours >= 12 ? 'PM' : 'AM';
    const adjustedHours = hours % 12 || 12;
    // Modified on 10/11/2025 - Added zero-padding to hours for consistent formatting (01:12 PM instead of 1:12 PM)
    // Old code: return `${adjustedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    return `${adjustedHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
  } catch (error) {
    return time;
  }
};


const DischargeSummaryPDF = ({ patient, admission }) => {
  const defaultDischargeInstructions = [
    "You might be sore for a few days but this will subside, if you need any medication for your pain you may contact as it anytime",
    "Do cold fomentation on the sore area",
    "Do not take part in any strenuous activites after the procedure for example heavy lifting, running etc..",
  ];

  const admissionData = admission?.admission || {};
  //Modification: 2025-11-21 - Render Course Of Hospital Stay and styling updates
  // Defensive fallback: if `course_of_hospital_stay` not provided upstream, compute from dates here for rendering
  const computeCourseOfStay = (() => {
    const provided = admissionData.course_of_hospital_stay || admissionData.course_of_hospital_stay_text;
    // Filter out null, "null", "NULL", or empty strings
    const providedStr = String(provided || '').trim().toLowerCase();
    if (provided && providedStr !== '' && providedStr !== 'null') return provided;
    
    // parse dates tolerant of '/' or '-'
    const safeParse = (d) => {
      if (!d) return null;
      const dt = new Date(String(d).replace(/\//g, '-'));
      return isNaN(dt) ? null : dt;
    };
    const a = safeParse(admissionData.date_of_admission);
    const b = safeParse(admissionData.date_of_discharge) || new Date();
    if (!a || !b) return null;
    const msPerDay = 1000 * 60 * 60 * 24;
    const diff = b - a;
    const days = Math.max(1, Math.floor(diff / msPerDay) + 1);
    return `${days} day${days > 1 ? 's' : ''}`;
  })();

  const admissionCourse = computeCourseOfStay || 'N/A';
  const dischargeMedicines = admissionData.discharge_medicines || [];
  const dischargeInstructionItems = defaultDischargeInstructions;
  
  // Filter out medications with null/empty medicine names
  const validMedicines = dischargeMedicines.filter(
    med => med.medicine_name && med.medicine_name.trim() !== "" && med.medicine_name.toLowerCase() !== "null"
  );
  
  const medicationList = validMedicines.map((med, index) => {
    const freq = med.frequency?.trim();
    const duration = med.duration_days?.trim();

    const instructions = [
      freq,
      duration && duration !== "null" && duration !== "" ? `for ${duration}` : ""
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      sno: index + 1,
      name: med.medicine_name || "N/A",
      m: med.morning ? "YES" : "NO",
      a: med.afternoon ? "YES" : "NO",
      n: med.night ? "YES" : "NO",
      instructions: instructions || "N/A"
    };
  });
  
  const currentDate = new Date().toISOString().split("T")[0];
  const currentTime = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  const [centerCode, setCenterCode] = React.useState("");

  React.useEffect(() => {
    const code = typeof window !== "undefined" ? localStorage.getItem("center_code") || "" : "";
    setCenterCode(code);
  }, []);

  let disclaimerImage = "/TeluguDisclaimer.png";

  if (centerCode.startsWith("HYD")) {
    disclaimerImage = "/TeluguDisclaimer.png";
  } else if (centerCode.startsWith("CHE")) {
    disclaimerImage = "/TamilDisclaimer.png";
  } else if (centerCode.startsWith("BAN")) {
    disclaimerImage = "/KannadaDisclaimer.png";
  }

  const resolvedDisclaimerImage =
    typeof window !== "undefined"
      ? `${window.location.origin}${disclaimerImage}`
      : disclaimerImage;

  return (
    <Document>
      {/* First Page: Content before Medication at Discharge */}
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>Discharge Summary</Text>
        <View style={styles.hr} />
        <Text style={[styles.boldfont, styles.dateRight]}>Date: {formatDate(admissionData.date_of_discharge || currentDate)}</Text>
        <View style={styles.patientInfoContainer}>
          <View style={styles.patientInfoLeft}>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>Name: {patient.title ? `${patient.title} ${patient.full_name}` : (patient.full_name || "_____________")}</Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>IP No: {patient.ip_no || "_____________"}</Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>Sex: {patient.gender || "_____________"}</Text>
            {/* <Text style={[styles.boldfont, styles.patientInfoItem]}>Occupation: {patient.occupation || "_____________"}</Text> */}
          </View>
          <View style={styles.patientInfoRight}>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>Age: {patient.age || "_____________"}</Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>UHID: {patient.uhid_no || "_____________"}</Text>
            <Text style={[styles.boldfont, styles.patientInfoItem]}>Phone: {patient.mobile || "_____________"}</Text>
            {/* <Text style={[styles.boldfont, styles.patientInfoItem]}>Religion: {patient.religion || "_____________"}</Text> */}
            <Text style={[styles.boldfont, styles.patientInfoItem]}>Identification Marks: {patient.identification_mark || "_____________"}</Text>
          </View>
        </View>
        <View style={styles.hr} />
        <View style={styles.dateContainer}>
          <View style={styles.dateColumn}>
            <Text style={[styles.boldfont, styles.text]}>Date of Admission: {formatDate(admissionData.date_of_admission) || "_____________"}</Text>
            {/* <Text style={[styles.boldfont, styles.text]}>Date of Procedure: {formatDate(admissionData.date_of_procedure) || "_____________"}</Text> */}
            <Text style={[styles.boldfont, styles.text]}>Date of Discharge: {formatDate(admissionData.date_of_discharge || currentDate)}</Text>
          </View>
          <View style={styles.dateColumnRight}>
            <Text style={[styles.boldfont, styles.text]}>Admission Time: {convertTo12HourFormat(admissionData.time_of_admission) || "_____________"}</Text>
            {/* <Text style={[styles.boldfont, styles.text]}>Procedure Time: {convertTo12HourFormat(admissionData.time_of_procedure) || "_____________"}</Text> */}
            <Text style={[styles.boldfont, styles.text]}>Discharge Time: {convertTo12HourFormat(admissionData.time_of_discharge) || currentTime}</Text>
          </View>
        </View>
        <View style={styles.hr} />

        {/* MODIFICATION: 2025-11-12 - Removed wrap={false} from sections with potentially long content
            OLD CODE: All sections had wrap={false}, causing blank pages when content exceeded limits
            NEW CODE: Removed wrap={false}, relying on keepWithNext in subtitle style to keep headings
            with first line. Only keeping wrap={false} for short sections like Surgeon.
        */}
        <View style={styles.section}>
          <Text style={styles.subtitle}>Pre-Procedure Diagnosis:</Text>
          <Text style={styles.text}>{admissionData.pre_procedure_diagnosis || ""}</Text>
        </View>

        <View style={styles.hr} />

        <View style={styles.section}>
          <Text style={styles.subtitle}>Details of Procedure:</Text>
          <Text style={styles.text}>{admissionData.detail_of_procedure || ""}</Text>
        </View>

        <View style={styles.hr} />

        {/* Keeping wrap={false} for short content */}
        <View wrap={false} style={styles.section}>
          <Text style={styles.subtitle}>Surgeon:</Text>
          <Text style={styles.text}>{admissionData.surgeon || ""}</Text>
        </View>

        <View style={styles.hr} />
{/*
        <View style={styles.section}>
          <Text style={styles.subtitle}>Discharge Instructions:</Text>
          {dischargeInstructionItems.length > 0
            ? dischargeInstructionItems.map((item, idx) => (
                <View key={idx} style={{ flexDirection: "row", alignItems: "flex-start", marginBottom: 5 }}>
                  <Text style={{ marginRight: 6, fontSize: 10 }}>•</Text>
                  <Text style={[styles.text, { flex: 1 }]}>{item}</Text>
                </View>
              ))
            : <Text style={styles.text}></Text>
          }
        </View>
        <View style={styles.hr} />
*/}
        {/* MODIFICATION: 2025-11-12 - Merged two pages into single page, fixed table headers
            OLD CODE: Separate <Page> components causing forced page break
            NEW CODE: All content in single page with natural flow, added 'fixed' to table headers
        */}

  {/* MODIFICATION: 2025-11-14 - Medication Table with fixed headers for pagination - Only render if valid medications exist */}
  {medicationList.length > 0 && (
  <View style={styles.section} wrap={false}>
          <Text style={styles.subtitle}>Medication at Discharge:</Text>
          <View style={styles.table}>
            {/* Group both header rows + first data row to prevent squish at page bottom */}
            <View wrap={false}>
              {/* Header Row 1 - Fixed so it repeats on page splits */}
              <View style={[styles.tableRow, styles.tableHeader]} fixed>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>S.No</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text>Medicine Name</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text>Dosage</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text>Instructions</Text>
                </View>
              </View>
              {/* Header Row 2 - Also fixed */}
              <View style={[styles.tableRow, styles.tableHeader]} fixed>
                <View style={[styles.tableCol, { width: "10%" }]}></View>
                <View style={[styles.tableCol, { width: "30%" }]}></View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>M</Text>
                </View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>A</Text>
                </View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>N</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}></View>
              </View>
              {/* First data row included in the non-wrapping group */}
              {medicationList.length > 0 && (
                <View key={medicationList[0].sno} wrap={false} style={styles.tableRow}>
                  <View style={[styles.tableCol, { width: "10%" }]}>
                    <Text>{medicationList[0].sno}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "30%" }]}>
                    <Text>{medicationList[0].name}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "10%" }]}>
                    <Text>{medicationList[0].m}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "10%" }]}>
                    <Text>{medicationList[0].a}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "10%" }]}>
                    <Text>{medicationList[0].n}</Text>
                  </View>
                  <View style={[styles.tableCol, { width: "30%" }]}>
                    <Text>{medicationList[0].instructions}</Text>
                  </View>
                </View>
              )}
            </View>
            {/* Remaining data rows - Keep wrap={false} to prevent individual row splits */}
            {medicationList.slice(1).map((med) => (
              <View key={med.sno} wrap={false} style={styles.tableRow}>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>{med.sno}</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text>{med.name}</Text>
                </View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>{med.m}</Text>
                </View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>{med.a}</Text>
                </View>
                <View style={[styles.tableCol, { width: "10%" }]}>
                  <Text>{med.n}</Text>
                </View>
                <View style={[styles.tableCol, { width: "30%" }]}>
                  <Text>{med.instructions}</Text>
                </View>
              </View>
            ))}
          </View>
          <View style={styles.hr} />
        </View> 
  )}


        {/* Removed wrap={false} for multi-line content */}
       {/* <View style={styles.section}>
          <Text style={styles.subtitle}>When to Obtain Urgent Care</Text>
          <Text style={styles.text}>• In case you have pain which is unbearable</Text>
          <Text style={styles.text}>• Bleeding</Text>
          <Text style={styles.text}>• Nausea or vomiting, please take your temperature, if it is above 100 degrees Fahrenheit please report</Text>
          <Text style={styles.text}>• If you feel warm, please take your temperature +91 846604444 / +91 8758758751</Text>
          <Text style={styles.text}>• In case of emergency please contact an emergency room at a hospital nearest to your home</Text>
          <Text style={styles.text}>• You can also report to an emergency</Text>
        </View>

        <View style={styles.hr} />
*/}
{/*
       
        <View style={styles.section}>
          
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <Text style={[styles.subtitle, { marginRight: 6, marginTop: 0, marginBottom: 0, lineHeight: 1.2 }]}>Course Of Hospital Stay:</Text>
            <Text style={[styles.text, { marginBottom: 0, lineHeight: 1.2 }]}>{admissionCourse}.</Text>
          </View>
        </View>

        <View style={styles.hr} />

        <View style={styles.section}>
          <Text style={styles.subtitle}>Follow-up Advice:</Text>
          <Text style={styles.text}>{admissionData.follow_up_advice || "N/A"}</Text>
        </View>

        <View style={styles.hr} />

        
        <View wrap={false} style={[styles.section, { flexDirection: "row", alignItems: "center" }]}>
          <Text style={styles.subtitle}>
            Review On: {formatDate(admissionData.review_date || admissionData.next_visit) || "______________"}
          </Text>
          <Text style={[styles.smallText, { marginLeft: 10, marginTop: 15 }]}>
            (For: {admissionData.reason_for_next_visit || "______________"})
          </Text>
        </View>

       
        {String(admissionData.disclaimer).toLowerCase() === "true" && (
          <View style={styles.section}>
            <Text style={styles.subtitle}>Disclaimer</Text>
            <Image style={styles.disclaimerImage} src={resolvedDisclaimerImage} />
          </View>
        )}

        <View style={styles.signatureArea}>
          <View>
            <Text style={styles.text}>Explained by: _____________________</Text>
          </View>
          <View>
            <Text style={styles.text}>Signature of patient:  ___________________</Text>
          </View>
        </View>
*/}
        <Text
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          style={styles.footer}
          fixed
        />
      </Page>
    </Document>
  );
};

export default DischargeSummaryPDF;
